/* ============================================================================
   REVIEWS — Figma 877:187321: the drawer (D 877:174114 · M 877:173860, the
   Filtered / Search / empty states beside them) and its filters (877:173552),
   opened from the page's REVIEWS link.
   It works like the Yotpo-style drawer on MeUndies: search, multi-select
   filters, sort, helpful votes, Read More and Load More, all live — on the
   shared drawer shell, with the PDP's motion vocabulary.
   The reviews are dummy copy written for the prototype (nothing is pulled
   from a live site) — the Catarina's here, the Isabel's in reviews-isabel.js.
   Each stands for a share of the product's total (53,010 · 14,720) so the
   counts read like the real thing: "Showing 10,893 reviews", not "Showing 4".
   ========================================================================== */

import { animate, stagger, MO, state, play, track, SPRING_RELEASE, $, $$ } from './mo.js';
import { createDrawer } from './drawers.js';
import { announce, tm } from './ui.js';
import isabelReviews from './reviews-isabel.js';
import setReviews from './reviews-set.js';

const PAGE = 6; // the desktop frame shows six cards above LOAD MORE

/* ---------------------------------------------------------------- data */
/* rating · title · body · date · name (null: anonymous, no details) · fit ·
   functionality · comfort · size · length · height (in) · weight (lb) ·
   role (shown) / occ (filter group) · helpful · media */
const R = (id, rating, title, body, date, who = null, scores = ['True to Size', 'High', 'High'], helpful = 0, media = []) => ({
  id, rating, title, body, date, helpful, media,
  fit: scores[0], func: scores[1], comfort: scores[2],
  ...(who ? { name: who[0], size: who[1], length: who[2], height: who[3], weight: who[4], role: who[5], occ: who[6] } : {}),
});

const CATARINA_REVIEWS = [
  R('r01', 5, 'My favorite top!', 'So flattering! I get so many compliments on this top. It has such a nice tailored fit, but I do find myself sizing down because the fabric has a lot of stretch. It holds its shape through a full twelve-hour shift and still looks crisp at the end.', '2026-09-21', ['Mariela V.', 'M', 'Regular', 62, 115, 'Nurse', 'Nursing'], undefined, 5),
  R('r02', 5, 'Finally, a V-neck that stays put', 'I have tried every brand on our unit and this is the first V-neck that doesn’t gape when I lean over a patient. The darted back makes it look tailored without feeling tight.', '2026-09-14', ['Danielle R.', 'S', 'Regular', 65, 132, 'ICU Nurse', 'Nursing'], undefined, 12, ['assets/catarina/navy/03.webp', 'assets/catarina/navy/05.webp']),
  R('r03', 4, 'Great scrub, runs a little long', 'Love the fabric and the color is gorgeous. I’m on the shorter side and the regular length hits a bit low on me, so I’ll try petite next time. Otherwise perfect.', '2026-09-09', ['Priya S.', 'XS', 'Regular', 61, 108, 'Dental Hygienist', 'Dental'], ['Runs Large', 'High', 'High'], 3),
  R('r04', 5, 'All love', 'Love the colour, love the fabric, love the fit, love the comfort.', '2026-09-02', ['Hannah K.', 'M', 'Regular', 66, 140, 'Physician Assistant', 'Physician'], undefined, 0),
  R('r05', 5, 'Survives the hospital laundry', 'I’ve washed mine at least forty times and it still looks new. No pilling, no fading, and it dries fast. The chest pocket fits my badge, pen light and a granola bar.', '2026-08-28', null, undefined, 7),
  R('r06', 5, 'Petite length is a game changer', 'As a 5’0” nurse I usually have to hem everything. The petite Catarina hits exactly where it should and the sleeves don’t swallow my arms.', '2026-08-19', ['Lena T.', 'XXS', 'Petite', 60, 98, 'Nurse', 'Nursing'], undefined, 9),
  R('r07', 4, 'Comfortable, wish there were more pockets', 'Fabric is soft and stretchy and I like how it looks. One pocket is clean, but on busy days I miss having somewhere to stash my phone. I pair it with cargo pants to make up for it.', '2026-08-11', ['Marcus J.', 'L', 'Regular', 71, 182, 'Surgical Tech', 'Tech'], ['True to Size', 'Alright', 'High'], 4),
  R('r08', 5, 'Looks expensive', 'Patients keep asking where my scrubs are from. The navy is deep and rich and the fabric drapes instead of clinging. Worth every penny.', '2026-08-03', null, undefined, 2, ['assets/catarina/navy/02.webp']),
  R('r09', 5, 'Best top for clinicals', 'Bought two for nursing school and ended up buying three more. They hold up to long clinical days and I never feel sloppy.', '2026-07-27', ['Ava M.', 'S', 'Regular', 64, 125, 'Nursing Student', 'Student'], undefined, 6),
  R('r10', 3.5, 'Too long', 'Although I am on the petite side, I feel like this top is too long for a regular. It’s bulky to tuck in and it looks a little awkward to leave out. I know they had petite sizes but I didn’t think I’d need them.', '2026-07-20', null, ['Runs Large', 'High', 'Alright'], 1),
  R('r11', 5, 'Royal blue is stunning', 'The royal blue is so vibrant in person, and it hasn’t faded at all after months of washing. The fit is classic, not boxy, and the V-neck is modest.', '2026-07-14', ['Jasmine W.', 'L', 'Tall', 70, 168, 'Physical Therapist', 'Specialize'], undefined, 8, ['assets/catarina/royal-blue/02.webp', 'assets/catarina/royal-blue/04.webp']),
  R('r12', 5, 'Stretch for days', 'I’m on my knees with kids all day and this top moves with me. Nothing rides up, nothing pulls across the back.', '2026-07-06', ['Carmen D.', 'M', 'Regular', 63, 150, 'Pediatric Nurse', 'Nursing'], undefined, 5),
  R('r13', 4, 'Runs a touch small in the shoulders', 'Gorgeous top and the fabric is the best I’ve worn. I have broad shoulders and needed to size up, so check the size chart if that’s you too.', '2026-06-29', ['Rachel B.', 'L', 'Regular', 68, 160, 'Veterinary Technician', 'Veterinarian'], ['Runs Small', 'High', 'High'], 3),
  R('r14', 5, 'Doesn’t wrinkle', 'I pull it out of the dryer and go. Even after sitting in my locker it looks pressed. Anti-wrinkle is not a gimmick here.', '2026-06-22', null, undefined, 4),
  R('r15', 5, 'Great for the OR', 'Lightweight enough under a gown, and the antimicrobial fabric means I don’t feel gross at hour ten. The pocket sits high enough to stay out of the way.', '2026-06-15', ['Imani O.', 'S', 'Regular', 66, 138, 'OR Nurse', 'Nursing'], undefined, 6),
  R('r16', 2, 'Not for me', 'The fabric is nice but the cut didn’t work on my body — the hem flares out and it felt short in the torso. Returning was easy at least.', '2026-06-08', ['Tess G.', 'XL', 'Regular', 69, 205, 'Medical Assistant', 'Other'], ['Runs Small', 'Alright', 'Alright'], 2),
  R('r17', 5, 'Black never looks washed out', 'The black stays black, which is rare. It’s my go-to for night shift because it hides everything and still looks sharp at rounds.', '2026-06-01', ['Simone A.', 'M', 'Regular', 67, 145, 'ER Nurse', 'Nursing'], undefined, 11, ['assets/catarina/black/03.webp', 'assets/catarina/black/05.webp']),
  R('r18', 5, 'Perfect teaching uniform', 'I teach clinicals and need to look put-together all day. These do it without feeling stiff. My students keep asking about them.', '2026-05-25', ['Laura P.', 'L', 'Regular', 65, 170, 'Clinical Instructor', 'Teacher'], undefined, 3),
  R('r19', 5, 'Size inclusive and flattering', 'So happy to find a top that fits well at 3XL without being a tent. The darted back gives it shape and the V-neck is flattering.', '2026-05-18', ['Nicole H.', '3XL', 'Regular', 66, 238, 'Nurse Practitioner', 'Nursing'], undefined, 14),
  R('r20', 3, 'Nice but pricey', 'Quality is good and it fits true to size. I just expected a little more for the price — I wish it came with a second pocket.', '2026-05-11', null, ['True to Size', 'Alright', 'High'], 1),
  R('r21', 5, 'Tall length, finally', 'At 6’1” every top ends up short on me. The tall Catarina actually covers my waistband when I reach for things.', '2026-05-04', ['Kendra L.', 'L', 'Tall', 73, 178, 'Respiratory Therapist', 'Specialize'], undefined, 7),
  R('r22', 5, 'The ceil blue is perfect', 'Our unit color is ceil and this is the nicest version I’ve found. Soft, breathable and it doesn’t show sweat.', '2026-04-27', ['Grace N.', 'XS', 'Regular', 62, 112, 'Nurse', 'Nursing'], undefined, 2),
  R('r23', 4, 'Good fit, gets a little warm', 'Fits beautifully and looks great on camera for telehealth. On really hot days it’s a bit warm, but I’d still buy it again.', '2026-04-19', ['Elena F.', 'M', 'Regular', 64, 142, 'Physician', 'Physician'], ['True to Size', 'High', 'Alright'], 2),
  R('r24', 5, 'Bought every color', 'I started with navy and now own six. Same great fit in every color, which says a lot about the consistency.', '2026-04-12', ['Brianna C.', 'S', 'Petite', 61, 120, 'Dental Assistant', 'Dental'], undefined, 9),
  R('r25', 5, 'Comfortable from the first wear', 'No break-in, no scratchy seams. It feels like a good T-shirt but looks like a uniform.', '2026-04-03', null, undefined, 3),
  R('r26', 5, 'Great for vet work', 'Fur doesn’t cling to it as much as cotton scrubs, and it washes clean every time. The pocket holds treats, so the dogs love it too.', '2026-03-27', ['Olivia Z.', 'M', 'Regular', 67, 155, 'Veterinarian', 'Veterinarian'], undefined, 5),
  R('r27', 4, 'Love it, sleeves a bit long', 'Overall a fantastic top. The sleeves come down a bit far on me, but the fit everywhere else is spot on.', '2026-03-19', ['Mei C.', 'XS', 'Regular', 60, 102, 'Pharmacist', 'Other'], ['True to Size', 'High', 'High'], 1),
  R('r28', 5, 'Holds up to twelve-hour shifts', 'Starts the shift looking sharp and ends it that way too. I don’t think about my top once all day, which is the whole point.', '2026-03-10', ['Destiny R.', '2XL', 'Regular', 65, 215, 'Nurse', 'Nursing'], undefined, 6),
  R('r29', 5, 'Easy to move in', 'Lots of stretch and the shoulders never pull when I reach overhead. Great for lifting and transferring patients.', '2026-02-28', ['Tom W.', 'L', 'Regular', 74, 190, 'Radiology Tech', 'Tech'], undefined, 2),
  R('r30', 5, 'Classic for a reason', 'Simple, clean and professional. Nothing fancy, it just works every single day.', '2026-02-16', null, undefined, 0),
];

/* the PDP's product → its reviews and their total */
const SETS = {
  catarina: () => ({ list: CATARINA_REVIEWS, total: 53010 }),
  isabel: () => ({ list: isabelReviews(R), total: 14720 }),
};

/* a bundle's products, one list each (the set page's tabs) */
let SET_LISTS = null;
const listFor = (id) => {
  SET_LISTS ||= { catarina: CATARINA_REVIEWS, isabel: isabelReviews(R), ...setReviews(R) };
  return SET_LISTS[id] || [];
};

/* each dummy review stands for a share of the total, by star bucket
   (90 / 7 / 2 / 1%), varied a little so filtered counts look organic */
function weigh(REVIEWS, TOTAL) {
  const SHARE = { 5: 0.9, 4: 0.07, 3: 0.02, 2: 0.01 };
  const groups = {};
  REVIEWS.forEach((r, i) => { (groups[Math.floor(r.rating)] ||= []).push([r, 1 + ((i * 37) % 11) / 7]); });
  let left = TOTAL;
  Object.entries(SHARE).forEach(([star, share], gi, all) => {
    const g = groups[star] || [];
    const target = gi === all.length - 1 ? left : Math.round(TOTAL * share);
    const raw = g.reduce((s, [, w]) => s + w, 0);
    let given = 0;
    g.forEach(([r, w], k) => { r.n = k === g.length - 1 ? target - given : Math.round((target * w) / raw); given += r.n; });
    left -= target;
  });
}

/* the filter groups (877:173561 · Filter Open) and how a review matches each */
const HEIGHTS = [['< 5’0', 0, 60], ['5’1 - 5’3', 61, 63], ['5’4 - 5’6', 64, 66], ['5’7 - 5’9', 67, 69],
  ['5’10 - 6’0', 70, 72], ['6’1 - 6’3', 73, 75], ['6’4 - 6’6', 76, 78], ['> 6’7', 79, 99]];
const WEIGHTS = [['Under 100 lbs', 0, 100], ['101-125 lbs', 101, 125], ['126-150 lbs', 126, 150], ['151-175 lbs', 151, 175],
  ['176-200 lbs', 176, 200], ['201-225 lbs', 201, 225], ['226-250 lbs', 226, 250], ['Over 251 lbs', 251, 999]];
const inRange = (ranges, v) => (label) => { const r = ranges.find((x) => x[0] === label); return v != null && v >= r[1] && v <= r[2]; };
const GROUPS = [
  { key: 'rating', title: 'Rating', options: ['1', '2', '3', '4', '5'], match: (rv, v) => Math.floor(rv.rating) === Number(v) },
  { key: 'size', title: 'Size Purchased', options: ['XXS', 'XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', '4XL', '5XL', '6XL'], match: (rv, v) => rv.size === v },
  { key: 'height', title: 'Height', options: HEIGHTS.map((h) => h[0]), match: (rv, v) => inRange(HEIGHTS, rv.height)(v) },
  { key: 'weight', title: 'Weight', options: WEIGHTS.map((w) => w[0]), match: (rv, v) => inRange(WEIGHTS, rv.weight)(v) },
  { key: 'occ', title: 'Occupation', options: ['Dental', 'Nursing', 'Physician', 'Specialize', 'Student', 'Teacher', 'Tech', 'Veterinarian', 'Other'], match: (rv, v) => rv.occ === v },
];

const SORTS = [
  ['featured', 'Featured'], ['helpful', 'Most Helpful'], ['recent', 'Most Recent'],
  ['high', 'Highest to Lowest'], ['low', 'Lowest to Highest'], ['media', 'With Media Only'],
];

/* ------------------------------------------------------------- markup */
const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
const fmtDate = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
const fmtHeight = (inches) => `${Math.floor(inches / 12)}’${inches % 12}”`;
const fmtCount = (n) => n.toLocaleString('en-US');
const TRUNC = 120;

export function starsHTML(rating, cls = 'rv-stars') {
  const full = Math.floor(rating);
  const half = rating - full >= 0.5;
  const icon = (f) => `<img src="assets/figma/reviews/star-${f}.svg" alt="" width="16" height="16">`;
  return `<span class="${cls}" role="img" aria-label="Rated ${rating} out of 5">${
    icon('full').repeat(full)}${half ? icon('half') : ''}${icon('empty').repeat(5 - full - (half ? 1 : 0))}</span>`;
}

const detail = (label, value) => (value ? `<p class="rv-card__kv"><span>${label}:</span> <b>${esc(value)}</b></p>` : '');

function cardHTML(rv, { expanded, voted }) {
  const long = rv.body.length > TRUNC + 20;
  const cut = long ? rv.body.slice(0, rv.body.lastIndexOf(' ', TRUNC)) : rv.body;
  const body = long && !expanded ? `${esc(cut)}…` : esc(rv.body);
  const more = long
    ? ` <button class="rv-card__more" type="button" aria-expanded="${expanded}" data-rv-more-text="${rv.id}">${expanded ? 'Read Less' : 'Read More'}</button>`
    : '';
  const helpful = rv.helpful + (voted ? 1 : 0);
  return `
    <article class="rv-card" data-rv-card="${rv.id}" tabindex="-1" aria-label="${esc(rv.title)}, ${rv.rating} stars">
      <div class="rv-card__top">${starsHTML(rv.rating)}<time class="rv-card__date" datetime="${rv.date}">${fmtDate(rv.date)}</time></div>
      <h3 class="rv-card__title">${esc(rv.title)}</h3>
      <p class="rv-card__body">${body}${more}</p>
      ${rv.media.length ? `<div class="rv-card__media">${rv.media.map((src, i) => `<img src="${src}" alt="Customer photo ${i + 1}" width="72" height="96" loading="lazy" decoding="async">`).join('')}</div>` : ''}
      <div class="rv-card__scores">${detail('Fit', rv.fit)}${detail('Functionality', rv.func)}${detail('Comfort', rv.comfort)}</div>
      <p class="rv-card__by">${rv.name ? `<span class="rv-card__name">- ${esc(rv.name)}</span>` : ''}<span class="rv-card__badge">Verified Buyer<img src="assets/figma/reviews/verified.svg" alt="" width="16" height="16"></span></p>
      ${rv.name ? `<div class="rv-card__about">${detail('Size Purchased', rv.size)}${detail('Length', rv.length)}${detail('Height', fmtHeight(rv.height))}${detail('Weight', `${rv.weight} lbs`)}<div class="rv-card__wide">${detail('Occupation', rv.role)}</div></div>` : ''}
      <button class="rv-helpful" type="button" aria-pressed="${voted}" data-rv-helpful="${rv.id}"><img src="assets/figma/reviews/helpful.svg" alt="" width="12" height="12"><span>Helpful (<span data-rv-count>${helpful}</span>)</span></button>
    </article>`;
}

const groupHTML = (g) => `
  <section class="rv-group" data-rv-group="${g.key}" aria-labelledby="rv-g-${g.key}">
    <div class="rv-group__head">
      <h3 class="rv-group__title" id="rv-g-${g.key}">${g.title}</h3>
      <button class="rv-group__clear" type="button" hidden data-rv-group-clear="${g.key}" aria-label="Clear ${g.title} filters">
        <img src="assets/figma/reviews/circle-close.svg" alt="" width="12" height="12">Clear</button>
    </div>
    <div class="rv-group__pills">${g.options.map((o) => `
      <button class="rv-pill${g.key === 'rating' ? ' rv-pill--rating' : ''}" type="button" aria-pressed="false" data-rv-pill="${g.key}" data-value="${esc(o)}"${g.key === 'rating' ? ` aria-label="${o} star${o === '1' ? '' : 's'}"` : ''}>${esc(o)}${g.key === 'rating' ? '<svg viewBox="0 0 13 13" width="13" height="13" aria-hidden="true"><path d="M6.5.54 8.03 4.93l4.65.1-3.7 2.81 1.35 4.45L6.5 9.64l-3.83 2.65 1.35-4.45L.32 5.03l4.65-.1L6.5.54Z" fill="currentColor"/></svg>' : ''}</button>`).join('')}
    </div>
  </section>`;

/* ============================================================================
   THE DRAWER
   ========================================================================== */
/* `tabs` (a bundle): one tab per product — { id, name, total, thumb() } —
   switching the list, its total, the product row and the thumbnail. Search,
   filters and sort carry across tabs; the list starts again at the top. */
export function initReviews({ product, getColor, tabs = null }) {
  let REVIEWS;
  let REVIEW_TOTAL;
  let active = null;
  const weighed = new Set();
  if (!tabs) {
    ({ list: REVIEWS, total: REVIEW_TOTAL } = (SETS[product?.id] || SETS.catarina)());
    weigh(REVIEWS, REVIEW_TOTAL);
  }
  const root = $('[data-drawer="reviews"]');
  const drawer = createDrawer(root);
  const scroller = $('[data-rv]', root);
  const list = $('[data-rv-list]', root);
  const total = $('[data-rv-total]', root);
  const q = $('[data-rv-q]', root);
  const qIcon = $('[data-rv-q-icon]', root);
  const qClear = $('[data-rv-q-clear]', root);
  const qMirror = $('[data-rv-q-mirror]', root);
  const filterBtn = $('[data-rv-filter-btn]', root);
  const filterLabel = $('[data-rv-filter-label]', root);
  const panel = $('[data-rv-filters]', root);
  const clearAll = $('[data-rv-clear-all]', root);
  const sortBtn = $('[data-rv-sort-btn]', root);
  const sortLabel = $('[data-rv-sort-label]', root);
  const sortList = $('[data-rv-sort-list]', root);
  const note = $('[data-rv-note]', root);
  const noteRule = $('[data-rv-note-rule]', root);
  const empty = $('[data-rv-empty]', root);
  const emptyHint = $('[data-rv-empty-hint]', root);
  const more = $('[data-rv-more]', root);
  const thumb = $('[data-rv-thumb]', root);
  const headTotal = $('[data-rv-head-total]', root);
  const productName = $('.rv__product-name', root);
  const tabList = $('[data-rv-tabs]', root);
  const setThumb = () => {
    /* the colour on the page, as its first gallery plate — a portrait shot,
       so the 74 × 96 frame shows the product, not a square crop of it. A
       bundle's tab shows that product in the page's colour. */
    const c = getColor?.();
    const src = active ? active.thumb() : (c?.images?.[0] || c?.card);
    if (src && thumb.getAttribute('src') !== src) thumb.src = src;
  };
  if (!tabs) headTotal.textContent = fmtCount(REVIEW_TOTAL);

  const sel = Object.fromEntries(GROUPS.map((g) => [g.key, new Set()]));
  const st = { q: '', sort: 'featured', shown: 0, voted: new Set(), expanded: new Set() };
  let results = [];

  panel.innerHTML = GROUPS.map(groupHTML).join('');
  sortList.innerHTML = SORTS.map(([key, label]) => `<li class="rv-sort__opt" role="option" id="rv-sort-${key}" aria-selected="${key === st.sort}" data-sort="${key}">${label}</li>`).join('');

  /* ---------------------------------------------------------- query */
  const activeCount = () => GROUPS.reduce((n, g) => n + sel[g.key].size, 0);
  const terms = () => st.q.toLowerCase().replace(/[^\p{L}\p{N}’' ]+/gu, ' ').split(/\s+/).filter(Boolean);
  function query() {
    const words = terms();
    let out = REVIEWS.filter((rv) => GROUPS.every((g) => !sel[g.key].size || [...sel[g.key]].some((v) => g.match(rv, v))));
    if (words.length) {
      out = out.filter((rv) => {
        const hay = `${rv.title} ${rv.body} ${rv.name || ''} ${rv.role || ''} ${rv.fit}`.toLowerCase();
        return words.every((w) => hay.includes(w));
      });
    }
    if (st.sort === 'media') out = out.filter((rv) => rv.media.length);
    const by = {
      helpful: (a, b) => b.helpful - a.helpful,
      recent: (a, b) => b.date.localeCompare(a.date),
      high: (a, b) => b.rating - a.rating || b.date.localeCompare(a.date),
      low: (a, b) => a.rating - b.rating || b.date.localeCompare(a.date),
    }[st.sort];
    return by ? [...out].sort(by) : out; // featured (and media) keep the curated order
  }

  /* ---------------------------------------------------------- render */
  const filtered = () => activeCount() > 0 || terms().length > 0;
  function syncChrome() {
    const n = activeCount();
    filterLabel.textContent = n ? `Filter (${n})` : 'Filter';
    clearAll.hidden = !n || filterBtn.getAttribute('aria-expanded') === 'true';
    GROUPS.forEach((g) => { $(`[data-rv-group-clear="${g.key}"]`, panel).hidden = !sel[g.key].size; });
    const searching = terms().length > 0;
    note.hidden = noteRule.hidden = !searching;
    qIcon.hidden = !!st.q;
    qClear.hidden = !st.q;
  }

  function cardsFrom(from, to) {
    return results.slice(from, to).map((rv) => cardHTML(rv, { expanded: st.expanded.has(rv.id), voted: st.voted.has(rv.id) })).join('');
  }

  /* a new result set: count, list and Load More, crossfaded so the list
     never hard-swaps under the eye */
  let renderToken = 0;
  async function refresh({ announceIt = true } = {}) {
    results = query();
    const count = results.reduce((s, rv) => s + rv.n, 0);
    syncChrome();
    total.textContent = filtered() ? `Showing ${fmtCount(count)} review${count === 1 ? '' : 's'}` : `All ${fmtCount(REVIEW_TOTAL)} Reviews`;
    if (announceIt) announce(results.length ? `Showing ${fmtCount(count)} reviews` : 'No matching reviews');
    const token = ++renderToken;
    if (list.childElementCount && !state.reduced) {
      await play(list, { opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit })?.finished;
      if (token !== renderToken) return;
    }
    st.shown = Math.min(PAGE, results.length);
    list.innerHTML = cardsFrom(0, st.shown);
    empty.hidden = results.length > 0;
    emptyHint.textContent = activeCount() ? 'Try clearing or changing the filters' : 'Try searching different keywords.';
    more.hidden = st.shown >= results.length;
    animate(list, { opacity: 1 }, { duration: 0 });
    reveal([...list.children]);
  }

  const reveal = (els) => {
    if (!els.length) return;
    play(els, { opacity: [0, 1], y: [MO.riseXs, 0] }, { duration: MO.base, ease: MO.eEntrance, delay: stagger(MO.stagTight) });
  };

  /* LOAD MORE appends the next six and hands focus to the first of them */
  more.addEventListener('click', () => {
    const from = st.shown;
    st.shown = Math.min(from + PAGE, results.length);
    list.insertAdjacentHTML('beforeend', cardsFrom(from, st.shown));
    const added = [...list.children].slice(from);
    reveal(added);
    added[0]?.focus({ preventScroll: true });
    added[0]?.scrollIntoView({ block: 'nearest', behavior: state.reduced ? 'auto' : 'smooth' });
    more.hidden = st.shown >= results.length;
    announce(`${added.length} more reviews`);
  });

  /* ---------------------------------------------------------- search */
  /* Figma sets the icon (and the clear button) right after the text, so the
     field is as wide as what's in it — the placeholder until something's
     typed — measured on a hidden copy in the same type */
  const fitQuery = () => {
    qMirror.textContent = q.value || q.placeholder;
    const w = qMirror.getBoundingClientRect().width;
    if (w) q.style.width = `${Math.ceil(w) + 2}px`;
  };
  $('.rv-search', root).addEventListener('click', (e) => { if (!e.target.closest('[data-rv-q-clear]')) q.focus(); });
  let qTimer = 0;
  q.addEventListener('input', () => {
    st.q = q.value;
    fitQuery();
    syncChrome();
    clearTimeout(qTimer);
    qTimer = setTimeout(() => refresh(), 250);
  });
  q.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { clearTimeout(qTimer); refresh(); }
    if (e.key === 'Escape' && q.value) { e.preventDefault(); clearSearch(); } // first Escape clears, the next closes
  });
  function clearSearch() {
    q.value = '';
    st.q = '';
    fitQuery();
    refresh();
    q.focus();
  }
  qClear.addEventListener('click', clearSearch);

  /* ---------------------------------------------------------- filters */
  function setPanel(open) {
    if ((filterBtn.getAttribute('aria-expanded') === 'true') === open) return;
    filterBtn.setAttribute('aria-expanded', String(open));
    if (open) setSort(false);
    syncChrome();
    if (open) {
      panel.hidden = false;
      if (state.reduced) return;
      const h = panel.scrollHeight;
      track(animate(panel, { height: [0, h], opacity: [0, 1] }, { duration: MO.base, ease: MO.eEntrance }))
        .finished.then(() => { panel.style.height = ''; });
      play([...panel.children], { opacity: [0, 1], y: [MO.riseXs, 0] }, { duration: MO.base, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: 0.06 }) });
    } else if (state.reduced) {
      panel.hidden = true;
    } else {
      track(animate(panel, { height: [panel.offsetHeight, 0], opacity: [1, 0] }, { duration: MO.quick, ease: MO.eExit }))
        .finished.then(() => { if (filterBtn.getAttribute('aria-expanded') !== 'true') { panel.hidden = true; panel.style.height = ''; } });
    }
  }
  filterBtn.addEventListener('click', () => setPanel(filterBtn.getAttribute('aria-expanded') !== 'true'));

  panel.addEventListener('click', (e) => {
    const pill = e.target.closest('[data-rv-pill]');
    const clear = e.target.closest('[data-rv-group-clear]');
    if (pill) {
      const set = sel[pill.dataset.rvPill];
      const on = !set.has(pill.dataset.value);
      if (on) set.add(pill.dataset.value); else set.delete(pill.dataset.value);
      pill.setAttribute('aria-pressed', String(on));
      play(pill, { scale: [MO.scalePress, 1] }, SPRING_RELEASE);
      refresh();
    } else if (clear) {
      const key = clear.dataset.rvGroupClear;
      sel[key].clear();
      $$(`[data-rv-pill="${key}"]`, panel).forEach((p) => p.setAttribute('aria-pressed', 'false'));
      $(`[data-rv-pill="${key}"]`, panel)?.focus();
      refresh();
    }
  });

  function clearFilters() {
    GROUPS.forEach((g) => sel[g.key].clear());
    $$('[data-rv-pill]', panel).forEach((p) => p.setAttribute('aria-pressed', 'false'));
    refresh();
  }
  clearAll.addEventListener('click', () => { clearFilters(); filterBtn.focus(); });

  /* ---------------------------------------------------------- sort */
  const opts = () => $$('[role="option"]', sortList);
  function setSort(open) {
    if ((sortBtn.getAttribute('aria-expanded') === 'true') === open) return;
    sortBtn.setAttribute('aria-expanded', String(open));
    if (open) {
      setPanel(false);
      sortList.hidden = false;
      const cur = $(`[data-sort="${st.sort}"]`, sortList);
      sortList.setAttribute('aria-activedescendant', cur.id);
      opts().forEach((o) => o.classList.toggle('is-active', o === cur));
      sortList.focus({ preventScroll: true });
      play(sortList, { opacity: [0, 1], y: [-4, 0] }, { duration: MO.quick, ease: MO.eEntrance });
    } else {
      sortList.hidden = true;
    }
  }
  function choose(key) {
    setSort(false);
    sortBtn.focus();
    if (key === st.sort) return;
    st.sort = key;
    opts().forEach((o) => o.setAttribute('aria-selected', String(o.dataset.sort === key)));
    sortLabel.textContent = `Sort: ${SORTS.find((s) => s[0] === key)[1]}`;
    refresh();
  }
  sortBtn.addEventListener('click', () => setSort(sortBtn.getAttribute('aria-expanded') !== 'true'));
  sortList.addEventListener('click', (e) => { const o = e.target.closest('[role="option"]'); if (o) choose(o.dataset.sort); });
  sortList.addEventListener('keydown', (e) => {
    const all = opts();
    const i = all.findIndex((o) => o.id === sortList.getAttribute('aria-activedescendant'));
    let n = null;
    if (e.key === 'ArrowDown') n = Math.min(all.length - 1, i + 1);
    else if (e.key === 'ArrowUp') n = Math.max(0, i - 1);
    else if (e.key === 'Home') n = 0;
    else if (e.key === 'End') n = all.length - 1;
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(all[i].dataset.sort); return; }
    else if (e.key === 'Escape') { e.preventDefault(); setSort(false); sortBtn.focus(); return; } // the menu, not the drawer
    else if (e.key === 'Tab') { setSort(false); return; }
    if (n === null) return;
    e.preventDefault();
    sortList.setAttribute('aria-activedescendant', all[n].id);
    all.forEach((o, k) => o.classList.toggle('is-active', k === n));
    all[n].scrollIntoView({ block: 'nearest' });
  });
  /* a click anywhere else closes the menu */
  root.addEventListener('pointerdown', (e) => {
    if (sortBtn.getAttribute('aria-expanded') === 'true' && !e.target.closest('[data-rv-sort]')) setSort(false);
  });

  /* ---------------------------------------------------------- cards */
  list.addEventListener('click', (e) => {
    const vote = e.target.closest('[data-rv-helpful]');
    const read = e.target.closest('[data-rv-more-text]');
    if (vote) {
      const id = vote.dataset.rvHelpful;
      const rv = REVIEWS.find((r) => r.id === id);
      const on = !st.voted.has(id);
      if (on) st.voted.add(id); else st.voted.delete(id);
      vote.setAttribute('aria-pressed', String(on));
      $('[data-rv-count]', vote).textContent = rv.helpful + (on ? 1 : 0);
      play(vote, { scale: [MO.scalePress, 1] }, SPRING_RELEASE);
      announce(on ? 'Marked helpful' : 'Helpful vote removed');
    } else if (read) {
      const id = read.dataset.rvMoreText;
      if (st.expanded.has(id)) st.expanded.delete(id); else st.expanded.add(id);
      const card = read.closest('[data-rv-card]');
      const rv = REVIEWS.find((r) => r.id === id);
      const next = document.createElement('div');
      next.innerHTML = cardHTML(rv, { expanded: st.expanded.has(id), voted: st.voted.has(id) });
      const body = $('.rv-card__body', next.firstElementChild);
      $('.rv-card__body', card).replaceWith(body);
      $('[data-rv-more-text]', body)?.focus();
      play(body, { opacity: [0.4, 1] }, { duration: MO.base, ease: MO.eEntrance });
    }
  });

  /* ---------------------------------------------------------- tabs */
  function use(tab, { announceIt = false } = {}) {
    active = tab;
    REVIEWS = listFor(tab.id);
    REVIEW_TOTAL = tab.total;
    if (!weighed.has(tab.id)) { weigh(REVIEWS, REVIEW_TOTAL); weighed.add(tab.id); }
    headTotal.textContent = fmtCount(REVIEW_TOTAL);
    productName.innerHTML = tm(tab.name);
    st.expanded.clear();
    $$('[role="tab"]', tabList).forEach((t) => {
      const on = t.dataset.rvTab === tab.id;
      t.setAttribute('aria-selected', String(on));
      t.tabIndex = on ? 0 : -1;
    });
    setThumb();
    refresh({ announceIt });
    if (announceIt) announce(`${tab.name}: ${fmtCount(REVIEW_TOTAL)} reviews`);
  }
  if (tabs) {
    tabList.hidden = false;
    tabList.innerHTML = tabs.map((t) => `
      <button class="rv-tab" type="button" role="tab" id="rv-tab-${t.id}" aria-selected="false" aria-controls="rv-panel" tabindex="-1" data-rv-tab="${t.id}">
        ${t.shortName}<sup class="rv-tab__count">${fmtCount(t.total)}</sup>
      </button>`).join('');
    list.id = 'rv-panel';
    list.setAttribute('role', 'tabpanel');
    tabList.addEventListener('click', (e) => {
      const t = e.target.closest('[data-rv-tab]');
      if (t && t.dataset.rvTab !== active.id) {
        use(tabs.find((x) => x.id === t.dataset.rvTab), { announceIt: true });
        play(t, { scale: [MO.scalePress, 1] }, SPRING_RELEASE);
      }
    });
    tabList.addEventListener('keydown', (e) => {
      const all = $$('[role="tab"]', tabList);
      const i = all.indexOf(document.activeElement);
      const d = { ArrowRight: 1, ArrowLeft: -1 }[e.key];
      let n = d ? (i + d + all.length) % all.length : e.key === 'Home' ? 0 : e.key === 'End' ? all.length - 1 : null;
      if (n === null || i < 0) return;
      e.preventDefault();
      all[n].focus();
      use(tabs[n], { announceIt: true });
    });
    use(tabs[0]);
  } else {
    setThumb();
    refresh({ announceIt: false });
  }

  return {
    /* a bundle opens on the tab it was last on, or the one asked for */
    open(trigger, { tab } = {}) {
      if (tabs && tab && tab !== active.id) use(tabs.find((t) => t.id === tab) || active);
      setThumb();
      setSort(false);
      drawer.open(trigger);
      scroller.scrollTop = 0;
      fitQuery();
      document.fonts?.ready.then(fitQuery);
      if (!state.reduced) {
        play([...$$('.rv__head, .rv__tabs:not([hidden]), .rv__controls', root), ...[...list.children].slice(0, 2)], { opacity: [0, 1], y: [MO.riseSm, 0] },
          { duration: MO.slow, ease: MO.eEntrance, delay: stagger(MO.stagTight, { startDelay: 0.16 }) });
      }
    },
    close: () => drawer.close(),
    get isOpen() { return drawer.isOpen; },
  };
}
